import {
  useCreateClubEvent,
  useCreateClubPoll,
  useCreateClubPost,
} from "@/api/club/useClub";
import { useTheme } from "@/theme/ThemeProvider";
import { useStyles } from "@/theme/styles";
import { AppButton } from "@/ui/components/AppButton";
import { TextField } from "@/ui/components/FormField";
import React, { useState } from "react";
import { Pressable, Text, View } from "react-native";

type Kind = "post" | "poll" | "event";

export function ClubComposer({
  organizationId,
  canCreateEvent,
  canMarkOfficial,
  disabled,
  defaultKind = "post",
  onCreated,
}: {
  organizationId: string;
  canCreateEvent?: boolean;
  canMarkOfficial?: boolean;
  disabled?: boolean;
  defaultKind?: Kind;
  onCreated?: (itemId?: string) => void;
}) {
  const { theme } = useTheme();
  const s = useStyles();
  const [kind, setKind] = useState<Kind>(
    defaultKind === "event" && !canCreateEvent ? "post" : defaultKind
  );
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [isOfficial, setIsOfficial] = useState(false);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [optionsText, setOptionsText] = useState("Ja\nNein");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [location, setLocation] = useState("");
  const [onlineUrl, setOnlineUrl] = useState("");
  const [allDay, setAllDay] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createPost = useCreateClubPost(organizationId);
  const createPoll = useCreateClubPoll(organizationId);
  const createEvent = useCreateClubEvent(organizationId);

  const pending =
    createPost.isPending || createPoll.isPending || createEvent.isPending;

  const kinds: Array<{ key: Kind; label: string; hide?: boolean }> = [
    { key: "post", label: "Beitrag" },
    { key: "poll", label: "Umfrage" },
    { key: "event", label: "Veranstaltung", hide: !canCreateEvent },
  ];

  const submit = async () => {
    setError(null);
    try {
      if (kind === "post") {
        if (!body.trim()) {
          setError("Text ist erforderlich.");
          return;
        }
        const data = await createPost.mutateAsync({
          title: title.trim() || null,
          body: body.trim(),
          isOfficial: canMarkOfficial ? isOfficial : false,
        });
        onCreated?.(typeof data === "string" ? data : (data as any)?.item_id);
        return;
      }
      if (kind === "poll") {
        const options = optionsText
          .split("\n")
          .map((o) => o.trim())
          .filter(Boolean);
        if (!title.trim() || options.length < 2) {
          setError("Titel und mindestens zwei Optionen erforderlich.");
          return;
        }
        const data = await createPoll.mutateAsync({
          title: title.trim(),
          body: body.trim() || null,
          allowMultiple,
          options,
        });
        onCreated?.(typeof data === "string" ? data : (data as any)?.item_id);
        return;
      }
      if (kind === "event") {
        if (!title.trim() || !startsAt.trim()) {
          setError("Titel und Beginn sind erforderlich.");
          return;
        }
        const startIso = new Date(startsAt).toISOString();
        const endIso = endsAt.trim()
          ? new Date(endsAt).toISOString()
          : null;
        const data = await createEvent.mutateAsync({
          title: title.trim(),
          description: body.trim() || null,
          startsAt: startIso,
          endsAt: endIso,
          allDay,
          locationText: location.trim() || null,
          onlineUrl: onlineUrl.trim() || null,
        });
        onCreated?.(typeof data === "string" ? data : (data as any)?.item_id);
      }
    } catch (e: any) {
      setError(e?.message ?? "Speichern fehlgeschlagen");
    }
  };

  return (
    <View style={{ gap: theme.space.md }}>
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: theme.space.sm }}>
        {kinds
          .filter((k) => !k.hide)
          .map((k) => (
            <Pressable
              key={k.key}
              onPress={() => setKind(k.key)}
              style={{
                paddingHorizontal: theme.space.md,
                paddingVertical: theme.space.sm,
                borderRadius: theme.radius.md,
                borderWidth: 1,
                borderColor:
                  kind === k.key ? theme.color.primary : theme.color.border,
                backgroundColor:
                  kind === k.key ? theme.color.accentBg : theme.color.surface,
              }}
            >
              <Text
                style={[
                  s.textBody,
                  {
                    fontWeight: kind === k.key ? "700" : "500",
                    color:
                      kind === k.key ? theme.color.primary : theme.color.text,
                  },
                ]}
              >
                {k.label}
              </Text>
            </Pressable>
          ))}
      </View>

      <TextField
        label={kind === "post" ? "Titel (optional)" : "Titel"}
        value={title}
        onChangeText={setTitle}
        editable={!disabled}
      />

      <TextField
        label={
          kind === "event"
            ? "Beschreibung"
            : kind === "poll"
              ? "Beschreibung (optional)"
              : "Text"
        }
        value={body}
        onChangeText={setBody}
        multiline
        numberOfLines={5}
        editable={!disabled}
      />

      {kind === "post" && canMarkOfficial ? (
        <Pressable
          onPress={() => setIsOfficial((v) => !v)}
          style={{ flexDirection: "row", alignItems: "center", gap: theme.space.sm }}
        >
          <Text style={s.textBody}>
            {isOfficial ? "☑" : "☐"} Als offizielle Mitteilung
          </Text>
        </Pressable>
      ) : null}

      {kind === "poll" ? (
        <>
          <TextField
            label="Optionen (eine pro Zeile)"
            value={optionsText}
            onChangeText={setOptionsText}
            multiline
            numberOfLines={4}
            editable={!disabled}
          />
          <Pressable
            onPress={() => setAllowMultiple((v) => !v)}
            style={{ flexDirection: "row", alignItems: "center", gap: theme.space.sm }}
          >
            <Text style={s.textBody}>
              {allowMultiple ? "☑" : "☐"} Mehrfachauswahl erlauben
            </Text>
          </Pressable>
        </>
      ) : null}

      {kind === "event" ? (
        <>
          <TextField
            label="Beginn (YYYY-MM-DD HH:MM)"
            value={startsAt}
            onChangeText={setStartsAt}
            placeholder="2026-09-03 18:00"
            editable={!disabled}
          />
          <TextField
            label="Ende (optional)"
            value={endsAt}
            onChangeText={setEndsAt}
            placeholder="2026-09-03 20:00"
            editable={!disabled}
          />
          <TextField
            label="Ort"
            value={location}
            onChangeText={setLocation}
            editable={!disabled}
          />
          <TextField
            label="Online-Link"
            value={onlineUrl}
            onChangeText={setOnlineUrl}
            editable={!disabled}
          />
          <Pressable
            onPress={() => setAllDay((v) => !v)}
            style={{ flexDirection: "row", alignItems: "center", gap: theme.space.sm }}
          >
            <Text style={s.textBody}>
              {allDay ? "☑" : "☐"} Ganztägig
            </Text>
          </Pressable>
        </>
      ) : null}

      {error ? (
        <Text style={[s.textBody, { color: theme.color.danger }]}>{error}</Text>
      ) : null}

      <AppButton
        loading={pending}
        disabled={disabled}
        onPress={() => void submit()}
      >
        Veröffentlichen
      </AppButton>
    </View>
  );
}
